// Greenhold SPA Sentry init. Story 1.8.
//
// Defaults to disabled when VITE_SENTRY_DSN is unset, so a fresh dev clone
// works without provisioning a DSN. PII scrubbing mirrors the backend
// implementation in server/src/sentry_init.rs (regex strips emails + 16-19
// digit number runs; allowlist redacts password/token/api_key extras).

import * as Sentry from "@sentry/react";

const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
const CARD_RE = /\b\d{13,19}\b/g;
const REDACTED_KEYS = new Set([
  "password",
  "password_hash",
  "token",
  "api_key",
  "secret",
]);

function scrubString(input: string): string {
  return input.replace(EMAIL_RE, "[email]").replace(CARD_RE, "[card]");
}

function scrubValue(value: unknown): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = REDACTED_KEYS.has(k.toLowerCase()) ? "[redacted]" : scrubValue(v);
    }
    return out;
  }
  return value;
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.message) {
    event.message = scrubString(event.message);
  }
  if (event.exception?.values) {
    for (const exc of event.exception.values) {
      if (exc.value) {
        exc.value = scrubString(exc.value);
      }
    }
  }
  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>;
  }
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b.message) {
        b.message = scrubString(b.message);
      }
      if (b.data) {
        b.data = scrubValue(b.data) as Record<string, unknown>;
      }
    }
  }
  return event;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.info("sentry disabled — VITE_SENTRY_DSN not set");
    return;
  }
  Sentry.init({
    dsn,
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? "dev",
    environment: import.meta.env.MODE,
    beforeSend: scrubEvent,
  });
}
